import { useQuery } from '@apollo/client';
import { t, useI18n } from '@i18n';
import { portfolio_bool_exp, StatusQuoAnalysisPortfolioFragment } from '@predium/client-graphql';
import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { useQueryStates } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchItemType } from '@/components/search/SearchBoxWithLazyLoading';
import {
  GET_PORTFOLIO_COUNT,
  GET_PORTFOLIOS_BY_IDS,
  GET_PORTFOLIOS_BY_SEARCHED_STRING,
} from '@/graphql/StatusQuoAnalysis.queries';
import { SEARCH_PARAMS } from '@/routes/search-params';
import { validateUrlParameterIds } from '@/sections/StatusQuoAnalysis/utils';

export const ALL_PORTFOLIOS_ITEM = (): SearchItemType => ({
  value: -1,
  label: t('General_AllPortfolios'),
  hasBottomDivider: true,
});

type PortfolioSelectorBaseOptions = {
  debouncedSearchTerm: string;
  defaultPortfolioId?: ReturnType<typeof ALL_PORTFOLIOS_ITEM>['value'];
  /** Merged with name search (and id-in for by-ids query) via `_and`. */
  portfolioWhere?: portfolio_bool_exp | null;
};

export type UsePortfolioSelectorOptions =
  | (PortfolioSelectorBaseOptions & { selectionMode?: 'url' })
  | (PortfolioSelectorBaseOptions & {
      selectionMode: 'local';
      portfolioIds: number[];
      onPortfolioIdsChange: (portfolioIds: number[]) => void;
    });

export const usePortfolioSelector = (options: UsePortfolioSelectorOptions, limit: number) => {
  const { t } = useI18n();
  const { debouncedSearchTerm, defaultPortfolioId, portfolioWhere } = options;
  const isLocal = options.selectionMode === 'local';
  const appliedFromParent = isLocal ? options.portfolioIds : undefined;
  const onPortfolioIdsChange = isLocal ? options.onPortfolioIdsChange : undefined;

  const { data: portfolioAggregate } = useQuery(GET_PORTFOLIO_COUNT);
  const portfolioCount = portfolioAggregate?.portfolio_aggregate?.aggregate?.count;
  const [urlQueryParams, setUrlQueryParams] = useQueryStates(SEARCH_PARAMS.statusQuoAnalysis.portfolio);
  const [portfolioIds, setPortfoliosIds] = useState<number[]>(
    isLocal ? (appliedFromParent ?? []) : (urlQueryParams?.portfolioIds ?? []),
  );

  const queryParams = useMemo(() => {
    if (isLocal) {
      const ids = appliedFromParent ?? [];
      return {
        ...urlQueryParams,
        portfolioIds: ids.length ? ids : null,
      };
    }
    return urlQueryParams;
  }, [isLocal, appliedFromParent, urlQueryParams]);

  const setQueryParams = useCallback(
    (updates: Parameters<typeof setUrlQueryParams>[0]) => {
      if (isLocal) {
        if (updates && typeof updates === 'object' && 'portfolioIds' in updates) {
          onPortfolioIdsChange?.(updates.portfolioIds ?? []);
        }
      } else {
        void setUrlQueryParams(updates);
      }
    },
    [isLocal, onPortfolioIdsChange, setUrlQueryParams],
  );

  useEffect(() => {
    if (isLocal) return;
    setPortfoliosIds(urlQueryParams?.portfolioIds ?? []);
  }, [isLocal, urlQueryParams]);

  useEffect(() => {
    if (!isLocal) return;
    setPortfoliosIds(appliedFromParent ?? []);
  }, [isLocal, appliedFromParent]);

  useEffect(() => {
    const appliedPortfolioIds = isLocal ? (appliedFromParent ?? []) : (urlQueryParams?.portfolioIds ?? []);
    if (appliedPortfolioIds.length === 0 && !isNil(defaultPortfolioId)) {
      if (isLocal) {
        onPortfolioIdsChange?.([defaultPortfolioId]);
      } else {
        void setUrlQueryParams({ ...urlQueryParams, portfolioIds: [defaultPortfolioId] });
      }
    }
  }, [
    defaultPortfolioId,
    isLocal,
    appliedFromParent,
    urlQueryParams?.portfolioIds,
    onPortfolioIdsChange,
    setUrlQueryParams,
    urlQueryParams,
  ]);

  const mapToPortfolioSelectOption = useCallback(
    (portfolio: StatusQuoAnalysisPortfolioFragment): SearchItemType => {
      return {
        value: portfolio.id,
        label: portfolio.name ?? t('StatusQuoAnalysisFilterBarPortfolio_NoName_WithId', { portfolioId: portfolio.id }),
      };
    },
    [t],
  );

  const mergePortfolioWhere = (base: portfolio_bool_exp, extra?: portfolio_bool_exp | null): portfolio_bool_exp => {
    if (!extra) return base;
    return { _and: [base, extra] };
  };
  const searchWhere = useMemo(
    () => mergePortfolioWhere({ name: { _ilike: `%${debouncedSearchTerm.trim()}%` } }, portfolioWhere),
    [debouncedSearchTerm, portfolioWhere],
  );

  const {
    data: portfoliosBySearchedString,
    loading,
    fetchMore,
    refetch,
  } = useQuery(GET_PORTFOLIOS_BY_SEARCHED_STRING, {
    variables: { offset: 0, limit, where: searchWhere },
    notifyOnNetworkStatusChange: true,
  });

  const portfolios = useMemo(() => {
    if (isNil(portfoliosBySearchedString)) return [];
    const uniquePortfolios = uniqBy(portfoliosBySearchedString.portfolio.map(mapToPortfolioSelectOption), 'value');
    uniquePortfolios.unshift(ALL_PORTFOLIOS_ITEM());

    return uniquePortfolios;
  }, [mapToPortfolioSelectOption, portfoliosBySearchedString]);

  const hasMorePortfolios = useMemo(
    () => !isNil(portfolioCount) && portfolioCount > portfolios.length - 1,
    [portfolioCount, portfolios.length],
  );

  const idsToFetch = useMemo(
    () =>
      difference(
        portfolioIds,
        portfolios.map((portfolio) => portfolio.value),
      ),
    [portfolioIds, portfolios],
  );

  const idsWhere = useMemo(
    () => mergePortfolioWhere({ id: { _in: idsToFetch } }, portfolioWhere),
    [idsToFetch, portfolioWhere],
  );

  const { data: portfoliosByIds, loading: loadingByIds } = useQuery(GET_PORTFOLIOS_BY_IDS, {
    variables: { where: idsWhere },
  });

  useEffect(() => {
    if (!isNil(portfoliosByIds)) {
      const validPortfolioIds = portfoliosByIds.portfolio.map((p) => p.id);
      validPortfolioIds.unshift(ALL_PORTFOLIOS_ITEM().value);
      const allLoadedIds = [...validPortfolioIds, ...portfolios.map((p) => p.value)];
      const validIds = validateUrlParameterIds(portfolioIds, allLoadedIds);

      if (portfolioIds.length > 0 && !isEqual(sortBy(validIds), sortBy(portfolioIds))) {
        void setQueryParams({
          ...urlQueryParams,
          portfolioIds: validIds,
        });
      }
    }
  }, [portfolioIds, portfolios, portfoliosByIds, setQueryParams, urlQueryParams]);

  const loadedPortfolios = useMemo(() => {
    if (isNil(portfoliosByIds)) return [];
    const mappedPortfolios = portfoliosByIds.portfolio.map(mapToPortfolioSelectOption);
    const uniqueLoadedPortfolios = sortBy(uniqBy([...mappedPortfolios, ...portfolios], 'value'), (item) =>
      item.label.toLocaleLowerCase(),
    );
    uniqueLoadedPortfolios.unshift(ALL_PORTFOLIOS_ITEM());
    return uniqueLoadedPortfolios;
  }, [mapToPortfolioSelectOption, portfolios, portfoliosByIds]);

  const loadMore = useCallback(
    async (newOffset: number, newDebouncedSearchTerm: string) => {
      if (!hasMorePortfolios) return;

      void fetchMore({
        variables: {
          offset: newOffset,
          limit,
          where: mergePortfolioWhere({ name: { _ilike: `%${newDebouncedSearchTerm.trim()}%` } }, portfolioWhere),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...fetchMoreResult,
            portfolio: [...prev.portfolio, ...fetchMoreResult.portfolio],
          };
        },
      });
    },
    [hasMorePortfolios, fetchMore, limit, portfolioWhere],
  );

  const selectedItems = useMemo(() => {
    if (isNil(portfolioIds) || portfolioIds.length === 0) return [];

    const selectedItems = portfolioIds
      .map((id) => loadedPortfolios.find((portfolio) => portfolio.value === id))
      .filter((p) => !isNil(p));

    const firstLoadedPortfolioItem = loadedPortfolios.at(0);
    if (selectedItems.length === 0 && !isNil(firstLoadedPortfolioItem)) return [firstLoadedPortfolioItem];

    return selectedItems;
  }, [portfolioIds, loadedPortfolios]);

  return {
    portfolios,
    loadedPortfolios,
    portfolioIds,
    setPortfoliosIds,
    loading,
    loadingByIds,
    hasMore: hasMorePortfolios,
    refetch,
    loadMore,
    selectedItems,
    queryParams,
    setQueryParams,
  };
};
